import { z } from 'zod';
import type { Role } from './constants';
import type { UserSummary } from './types';

/** Platform administration (the people who run Regent), served at the admin host only. */

export interface AdminOverviewDTO {
  workspaces: number;
  suspendedWorkspaces: number;
  users: number;
  suspendedUsers: number;
  tasks: number;
  files: number;
  storageBytes: number;
  /** People with a session touched in the last 24 hours. */
  activeUsers24h: number;
  signups7d: number;
  runningImports: number;
  recentWorkspaces: { id: string; name: string; plan: string; createdAt: number; members: number }[];
}

export interface AdminWorkspaceDTO {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  plan: string;
  members: number;
  tasks: number;
  files: number;
  storageBytes: number;
  lastActivityAt: number | null;
  createdAt: number;
  suspendedAt: number | null;
  deletedAt: number | null;
}

export interface AdminWorkspaceMemberDTO extends UserSummary {
  role: Role;
  joinedAt: number;
  lastSeenAt: number | null;
  suspendedAt: number | null;
}

export interface AdminWorkspaceDetailDTO extends AdminWorkspaceDTO {
  settings: Record<string, unknown>;
  taskPrefix: string;
  spaces: number;
  lists: number;
  docs: number;
  memberList: AdminWorkspaceMemberDTO[];
  invites: { id: string; email: string; role: Role; createdAt: number }[];
  recentActivity: AdminActivityDTO[];
}

export interface AdminUserDTO extends UserSummary {
  emailVerified: boolean;
  isPlatformAdmin: boolean;
  suspendedAt: number | null;
  createdAt: number;
  lastSeenAt: number | null;
  workspaces: number;
  sessions: number;
}

export interface AdminUserDetailDTO extends AdminUserDTO {
  memberships: { workspaceId: string; workspaceName: string; role: Role; joinedAt: number }[];
  sessionList: { id: string; ip: string | null; userAgent: string | null; createdAt: number; expiresAt: number; updatedAt: number }[];
  apiTokens: number;
  hasPassword: boolean;
  providers: string[];
}

export interface AdminActivityDTO {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  resourceType: string;
  resourceId: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  data: Record<string, unknown> | null;
  createdAt: number;
}

export interface AdminFileDTO {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  resourceType: string;
  resourceId: string;
  filename: string;
  mime: string;
  size: number;
  uploadedBy: string;
  uploaderName: string | null;
  createdAt: number;
  deletedAt: number | null;
}

export interface AdminJobDTO {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  source: string;
  status: 'running' | 'done' | 'failed';
  phase: string | null;
  counts: Record<string, number>;
  errors: number;
  error: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface AdminActionDTO {
  id: string;
  actorId: string;
  actorName: string | null;
  action: string;
  targetType: string;
  targetId: string;
  data: Record<string, unknown> | null;
  ip: string | null;
  createdAt: number;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

const pageQuery = {
  cursor: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  q: z.string().trim().max(120).optional(),
};

export const adminListQuerySchema = z.object(pageQuery);

export const adminWorkspacesQuerySchema = z.object({
  ...pageQuery,
  state: z.enum(['active', 'suspended', 'deleted', 'all']).optional(),
});

export const adminUsersQuerySchema = z.object({
  ...pageQuery,
  state: z.enum(['active', 'suspended', 'admins', 'all']).optional(),
});

export const adminActivityQuerySchema = z.object({
  ...pageQuery,
  workspaceId: z.string().max(64).optional(),
  actorId: z.string().max(64).optional(),
  resourceType: z.string().max(32).optional(),
});

export const adminFilesQuerySchema = z.object({
  ...pageQuery,
  workspaceId: z.string().max(64).optional(),
  mime: z.string().max(40).optional(),
  includeDeleted: z.coerce.boolean().optional(),
});

export const adminUpdateWorkspaceSchema = z.object({
  plan: z.string().trim().min(1).max(40).optional(),
  suspended: z.boolean().optional(),
  name: z.string().trim().min(1).max(120).optional(),
});

export const adminUpdateUserSchema = z.object({
  suspended: z.boolean().optional(),
  name: z.string().trim().min(1).max(120).optional(),
});

export type AdminUpdateWorkspaceInput = z.infer<typeof adminUpdateWorkspaceSchema>;
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;
